/**
 * Directive v-reveal — memunculkan elemen saat masuk ke area pandang.
 *
 * Memakai IntersectionObserver, bukan event scroll. Event scroll menyala
 * puluhan kali per detik dan memaksa pembacaan tata letak, sehingga gulir
 * terasa tersendat. IntersectionObserver dijalankan peramban di luar thread
 * utama dan hanya memberi kabar ketika ambang batas benar-benar terlampaui.
 *
 * Pemakaian:
 *   <div v-reveal>                      arah bawaan (naik)
 *   <div v-reveal="'left'">             masuk dari kiri
 *   <div v-reveal:120>                  jeda 120 milidetik
 *   <div v-reveal:80="'scale'">         gabungan keduanya
 *   <div v-reveal.once>                 hanya sekali (bawaan sudah sekali)
 *   <div v-reveal.repeat>               beranimasi lagi tiap kali terlihat
 */

const ARAH_VALID = ['up', 'down', 'left', 'right', 'scale', 'blur'];

// Satu observer dipakai bersama seluruh elemen — jauh lebih ringan
// dibanding membuat satu observer per elemen.
let observer = null;
const terdaftar = new WeakMap();

function pastikanObserver() {
  if (observer || typeof IntersectionObserver === 'undefined') return;

  observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const opsi = terdaftar.get(entry.target);
        if (!opsi) return;

        if (entry.isIntersecting) {
          entry.target.setAttribute('data-reveal', 'visible');
          if (!opsi.repeat) {
            observer.unobserve(entry.target);
            terdaftar.delete(entry.target);
          }
        } else if (opsi.repeat) {
          entry.target.setAttribute('data-reveal', '');
        }
      });
    },
    {
      // Dipicu sedikit sebelum elemen benar-benar terlihat, supaya animasi
      // sudah berjalan saat pengguna sampai ke sana.
      rootMargin: '0px 0px -12% 0px',
      threshold: 0.08,
    }
  );
}

export const reveal = {
  mounted(el, binding) {
    // Kalau pengguna meminta pengurangan gerak, tampilkan langsung.
    const kurangiGerak =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (kurangiGerak || typeof IntersectionObserver === 'undefined') {
      el.setAttribute('data-reveal', 'visible');
      return;
    }

    const arah = ARAH_VALID.includes(binding.value) ? binding.value : 'up';
    const jeda = binding.arg ? parseInt(binding.arg, 10) : 0;

    el.setAttribute('data-reveal', '');
    el.setAttribute('data-reveal-dir', arah);
    if (jeda) el.style.setProperty('--reveal-delay', `${jeda}ms`);

    terdaftar.set(el, { repeat: Boolean(binding.modifiers.repeat) });

    pastikanObserver();
    observer.observe(el);

    // Jika elemen sudah berada dalam viewport saat dipasang, langsung jadikan visible
    // agar konten penting di bagian atas halaman tidak pernah macet atau miring
    try {
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight && rect.bottom > 0) {
        requestAnimationFrame(() => {
          el.setAttribute('data-reveal', 'visible');
        });
      }
    } catch (e) {
      // abaikan jika tidak ada getBoundingClientRect
    }
  },

  unmounted(el) {
    if (observer && terdaftar.has(el)) {
      observer.unobserve(el);
      terdaftar.delete(el);
    }
  },
};

/**
 * Directive v-counter — menghitung angka naik saat elemen terlihat.
 *
 * Memakai requestAnimationFrame dengan pelambatan di akhir, bukan setInterval,
 * supaya sinkron dengan laju penyegaran layar dan tidak membebani prosesor.
 *
 * Pemakaian:  <span v-counter="1250">0</span>
 */
export const counter = {
  mounted(el, binding) {
    const target = Number(binding.value) || 0;
    const durasi = Number(binding.arg) || 1600;

    const kurangiGerak =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (kurangiGerak || typeof IntersectionObserver === 'undefined') {
      el.textContent = target.toLocaleString('id-ID');
      return;
    }

    el.textContent = '0';
    let sudahJalan = false;

    const obs = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting || sudahJalan) return;
        sudahJalan = true;
        obs.disconnect();

        const mulai = performance.now();

        const langkah = (sekarang) => {
          const t = Math.min((sekarang - mulai) / durasi, 1);
          // easeOutExpo — cepat di awal lalu melandai, terasa "mendarat"
          const e = t === 1 ? 1 : 1 - Math.pow(2, -10 * t);
          el.textContent = Math.round(target * e).toLocaleString('id-ID');
          if (t < 1) requestAnimationFrame(langkah);
        };

        requestAnimationFrame(langkah);
      },
      { threshold: 0.4 }
    );

    obs.observe(el);
  },
};

/**
 * Directive v-tilt — kartu miring mengikuti kursor.
 * Memberi kesan kedalaman tanpa pustaka tambahan.
 */
export const tilt = {
  mounted(el, binding) {
    const kurangiGerak =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Perangkat sentuh tidak punya kursor melayang, jadi dilewati.
    const adaHover = window.matchMedia && window.matchMedia('(hover: hover)').matches;
    if (kurangiGerak || !adaHover) return;

    const maks = Number(binding.value) || 6;
    let rafId = null;

    const gerak = (e) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left) / r.width - 0.5;
        const y = (e.clientY - r.top) / r.height - 0.5;
        el.style.transform =
          `perspective(900px) rotateX(${-y * maks}deg) rotateY(${x * maks}deg) translateY(-4px)`;
        rafId = null;
      });
    };

    const keluar = () => {
      if (rafId) { cancelAnimationFrame(rafId); rafId = null; }
      el.style.transform = '';
    };

    el.style.transition = 'transform 400ms cubic-bezier(0.16, 1, 0.3, 1)';
    el.addEventListener('mousemove', gerak);
    el.addEventListener('mouseleave', keluar);

    el._tiltCleanup = () => {
      el.removeEventListener('mousemove', gerak);
      el.removeEventListener('mouseleave', keluar);
      if (rafId) cancelAnimationFrame(rafId);
    };
  },

  unmounted(el) {
    // Tanpa pembersihan ini, listener menumpuk setiap kali komponen dipasang ulang.
    if (el._tiltCleanup) el._tiltCleanup();
  },
};

export default {
  install(app) {
    app.directive('reveal', reveal);
    app.directive('counter', counter);
    app.directive('tilt', tilt);
  },
};
